import { Component, Input } from '@angular/core';
import { BaseCellComponent } from '../base-cell.component';
import { ViewManager, PdfRowElement, PdfDateTimeValue } from 'src/app/board/pages/admin/admin-components/custom-views-config/view-builder/models/view-builder.models';
import { DatePipe } from '@angular/common';

interface ResolvedElement {
  displayValue: string;
}

@Component({
  selector: 'bhub-pdf-row-cell',
  standalone: false,
  templateUrl: './pdf-row-cell.component.html',
  styleUrls: ['./pdf-row-cell.component.scss'],
  providers: [DatePipe]
})
export class PdfRowCellComponent extends BaseCellComponent {
  @Input() viewManager: ViewManager | null = null;

  fontSize: number = 12;
  fontWeight: string = 'normal';
  fontStyle: string = 'normal';
  color: string = '#000000';
  textAlign: string = 'left';
  resolvedElements: ResolvedElement[] = [];

  private relativeDateOptions: { value: string; days?: number; months?: number; years?: number }[] = [
    { value: 'today', days: 0 },
    { value: 'yesterday', days: -1 },
    { value: 'tomorrow', days: 1 },
    { value: 'last7days', days: -7 },
    { value: 'last30days', days: -30 },
    { value: 'last90days', days: -90 },
    { value: 'lastyear', years: -1 },
    { value: 'next7days', days: 7 },
    { value: 'next30days', days: 30 },
    { value: 'next90days', days: 90 },
    { value: 'nextyear', years: 1 }
  ];

  constructor(private datePipe: DatePipe) {
    super();
  }

  protected initializeCell(): void {
    this.updateDisplayProperties();
  }

  protected onCellOrDataChange(): void {
    this.updateDisplayProperties();
  }

  protected onCellUpdate(): void {
    this.updateDisplayProperties();
  }

  protected onDataSet(data: any): void {
    this.updateDisplayProperties();
  }

  reloadData(): void {
    this.updateDisplayProperties();
  }

  private updateDisplayProperties(): void {
    if (!this.cell) return;

    this.fontSize = this.getConfigValue('pdfRowContainerFontSize', 12);
    this.fontWeight = this.getConfigValue('pdfRowContainerFontWeight', 'normal');
    this.fontStyle = this.getConfigValue('pdfRowContainerFontStyle', 'normal');
    this.color = this.getConfigValue('pdfRowContainerColor', '#000000');
    this.textAlign = this.getConfigValue('pdfRowContainerAlign', 'left');

    const elements: PdfRowElement[] = this.getConfigValue('pdfRowContainerElements', []);
    this.resolvedElements = elements.map(elem => this.resolveElement(elem));
  }

  private resolveElement(element: PdfRowElement): ResolvedElement {
    let displayValue = '';

    switch (element.type) {
      case 'text':
        displayValue = element.textValue || '';
        break;

      case 'field':
        displayValue = this.resolveFieldValue(element);
        break;

      case 'datetime':
        displayValue = this.resolveDateTimeValue(element);
        break;
    }

    return { displayValue };
  }

  private resolveFieldValue(element: PdfRowElement): string {
    const contextVariable = element.contextVariable;
    const fieldName = element.fieldName;
    const format = element.fieldFormat || '';

    if (!contextVariable || !fieldName) {
      return '[Configure field]';
    }

    if (!this.viewManager?.contextData) {
      return `[${contextVariable} not available]`;
    }

    const dataObject = this.viewManager.contextData[contextVariable];
    if (!dataObject) {
      return `[${contextVariable} not loaded]`;
    }

    let value: any;
    if (Array.isArray(dataObject) && dataObject.length > 0) {
      value = dataObject[0][fieldName];
    } else if (typeof dataObject === 'object') {
      value = dataObject[fieldName];
    }

    return this.formatValue(value, format);
  }

  private formatValue(value: any, format: string): string {
    if (value === null || value === undefined) {
      return '';
    }

    if (format === 'currency' && typeof value === 'number') {
      return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(value);
    }

    if (format === 'date' && (value instanceof Date || typeof value === 'string')) {
      return new Date(value).toLocaleDateString();
    }

    if (format === 'number' && typeof value === 'number') {
      return new Intl.NumberFormat('en-US').format(value);
    }

    if (format === 'percent' && typeof value === 'number') {
      return new Intl.NumberFormat('en-US', { style: 'percent', minimumFractionDigits: 0 }).format(value);
    }

    return String(value);
  }

  private resolveDateTimeValue(element: PdfRowElement): string {
    const dateTimeValue = element.dateTimeValue;
    const format = element.dateTimeFormat || 'MMMM d, yyyy';

    if (!dateTimeValue?.value) {
      return '[Configure date]';
    }

    const calculatedDate = this.calculateDate(dateTimeValue);
    if (calculatedDate) {
      return this.datePipe.transform(calculatedDate, format) || calculatedDate.toLocaleDateString();
    }

    return '[Invalid date]';
  }

  private calculateDate(dateValue: PdfDateTimeValue): Date | null {
    if (dateValue.mode === 'specific') {
      if (dateValue.value) {
        const date = dateValue.value instanceof Date ? dateValue.value : new Date(dateValue.value);
        if (!isNaN(date.getTime())) {
          return date;
        }
      }
      return null;
    }

    // Relative mode
    const today = new Date();

    if (dateValue.value === 'custom') {
      const result = new Date(today);
      if (dateValue.customDays) {
        result.setDate(result.getDate() + dateValue.customDays);
      }
      if (dateValue.customMonths) {
        result.setMonth(result.getMonth() + dateValue.customMonths);
      }
      if (dateValue.customYears) {
        result.setFullYear(result.getFullYear() + dateValue.customYears);
      }
      return result;
    }

    // Predefined relative option
    const option = this.relativeDateOptions.find(opt => opt.value === dateValue.value);
    if (option) {
      const result = new Date(today);
      if (option.days) {
        result.setDate(result.getDate() + option.days);
      }
      if (option.months) {
        result.setMonth(result.getMonth() + option.months);
      }
      if (option.years) {
        result.setFullYear(result.getFullYear() + option.years);
      }
      return result;
    }

    return null;
  }
}
